import React from 'react'
import { uniq } from 'lodash'
import {
  useApiGet,
  navigateTo,
  isBlank,
  map,
  SmallLoadable,
  AdvertiserStatus,
  AssigneeList,
  formatDiff,
  formatCurrencyReversed,
} from '../../shared'

export const TableValue = (props) => {
  const {
    values,
    rawValues,
    row,
    column,
    networks,
    assignees,
    statuses,
    adsStats,
  } = props

  const noPrev = !values.prev || !values.prev[column]

  if (
    column === 'conversionCount' ||
    column === 'rejectedCount' ||
    column === 'pageviewCount' ||
    column === 'clickCount' ||
    column === 'grossConversionCount'
  ) {
    if (noPrev) {
      return (
        <div className={`value-badge value-badge-${column}`}>
          {values[column]}
        </div>
      )
    } else {
      return (
        <>
          <div className={`value-badge value-badge-${column}`}>
            {values[column]}
          </div>
          <div className="row row-center row-end row-nowrap m-t-1">
            <div className="text-smallest text-light o-70">
              {formatDiff(values[column], values.prev[column])}
            </div>
            <div className={`value-badge value-badge-${column} value-badge-sm`}>
              {values.prev[column]}
            </div>
          </div>
        </>
      )
    }
  } else if (
    column === 'conversionCommission' ||
    column === 'conversionClickCommission' ||
    column === 'conversionTotalCommission' ||
    column === 'conversionPendingCommission' ||
    column === 'conversionRejectedCommission'
  ) {
    if (noPrev) {
      return (
        <div className="value-badge value-badge-conversionCommission text-nowrap">
          {values[column]}
        </div>
      )
    } else {
      return (
        <>
          <div className="value-badge value-badge-conversionCommission text-nowrap">
            {values[column]}
          </div>
          <div className="row row-center row-end row-nowrap m-t-1">
            <div className="text-smallest text-light o-70">
              {formatDiff(values[column], values.prev[column])}
            </div>
            <div className="value-badge value-badge-conversionCommission value-badge-sm text-nowrap">
              {values.prev[column]}
            </div>
          </div>
        </>
      )
    }
  } else if (column === 'conversionBoostAddedValue') {
    return (
      <div className="column column-right column-fill">
        <div className="value-badge value-badge-conversionCommission text-nowrap">
          {values.conversionBoostAddedValue}
        </div>
        <div className="row row-center row-end row-nowrap m-t-1">
          <div className="value-badge value-badge-conversionCommission value-badge-sm text-nowrap">
            {values.conversionUnboostedCommission}
          </div>
        </div>
      </div>
    )
  } else if (column === 'networks') {
    if (networks && values.networkUuids?.length) {
      const shownNetworks = networks.filter((network) =>
        values.networkUuids.includes(network.networkUuid)
      )
      return (
        <div className="row row-narrow row-nowrap">
          {map(shownNetworks, (network) => (
            <div
              key={network.networkUuid}
              className="badge text-smaller badge-hoverable text-bold"
              onClick={(event) => {
                navigateTo(event, `/networks/${network.networkUuid}`)
              }}
            >
              {network.networkName}
            </div>
          ))}
        </div>
      )
    } else {
      return null
    }
  } else if (column === 'trackers') {
    if (values.trackerUuids?.length) {
      return <TrackerList trackerUuids={values.trackerUuids} />
    } else {
      return null
    }
  } else if (column === 'assignees') {
    return <AssigneeList domain={row} assignees={assignees} />
  } else if (column === 'advertiserStatus') {
    const partnerStatus = statuses?.find(
      (advertiserStatus) =>
        advertiserStatus.advertiserStatusAdvertiserDomain === row &&
        advertiserStatus.advertiserStatusType === 'partner'
    )
    const dealStatus = statuses?.find(
      (advertiserStatus) =>
        advertiserStatus.advertiserStatusAdvertiserDomain === row &&
        advertiserStatus.advertiserStatusType === 'deal'
    )
    return (
      <div className="row row-narrow row-nowrap">
        {partnerStatus ? (
          <AdvertiserStatus
            advertiserUuid={partnerStatus.advertiserStatusAdvertiserUuid}
            initialStatus={partnerStatus.advertiserStatusValue}
            statusType={partnerStatus.advertiserStatusType}
            tooltipId="table-tooltip"
          />
        ) : (
          <AdvertiserStatus
            advertiserDomain={row}
            initialStatus={''}
            statusType="partner"
            tooltipId="table-tooltip"
          />
        )}
        {dealStatus ? (
          <AdvertiserStatus
            advertiserUuid={dealStatus.advertiserStatusAdvertiserUuid}
            initialStatus={dealStatus.advertiserStatusValue}
            statusType={dealStatus.advertiserStatusType}
            tooltipId="table-tooltip"
          />
        ) : (
          <AdvertiserStatus
            advertiserDomain={row}
            initialStatus={''}
            statusType="deal"
            tooltipId="table-tooltip"
          />
        )}
      </div>
    )
  } else if (column === 'activeSinceDatetime') {
    return values[column]
  } else if (column === 'googleAdsCost') {
    if (!row) return null
    const stat = adsStats?.find((stat) => stat.domain === row)
    return (
      <SmallLoadable data={adsStats}>
        {stat ? formatCurrencyReversed(stat.cost, stat.currencyCode) : null}
      </SmallLoadable>
    )
  } else if (column === 'googleAdsRoas') {
    if (!row) return null
    const stat = adsStats?.find((stat) => stat.domain === row)
    return (
      <SmallLoadable data={adsStats}>
        {rawValues?.conversionTotalCommission && stat?.cost
          ? formatCurrencyReversed(
              rawValues.conversionTotalCommission / stat.cost,
              stat.currencyCode
            )
          : null}
      </SmallLoadable>
    )
  } else {
    if (noPrev) {
      return values[column]
    } else {
      return (
        <>
          <div>{values[column]}</div>
          <div className="row row-center row-end row-nowrap m-t-1">
            <div className="text-smallest text-light o-70">
              {formatDiff(values[column], values.prev[column])}
            </div>
            <div className="text-smaller text-light">{values.prev[column]}</div>
          </div>
        </>
      )
    }
  }
}

const TrackerList = (props) => {
  try {
    const { trackerUuids } = props

    const trackers = useApiGet<{ trackerUuid: string; trackerName: string }[]>(
      '/advs/v1/camps/list',
      {
        trackerUuids: trackerUuids,
      }
    )

    if (isBlank(trackers)) return null
    const trackerNames = uniq(
      trackers.map((tracker) => tracker.trackerName)
    ).sort()
    if (!trackerNames.length) return null

    return (
      <SmallLoadable data={trackers}>
        <div className="row row-narrow row-nowrap">
          {trackerNames.map((trackerName) => (
            <div key={trackerName} className="badge text-smaller text-bold">
              {trackerName}
            </div>
          ))}
        </div>
      </SmallLoadable>
    )
  } catch (error) {
    console.error(error)
    return null
  }
}
